import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import router from "../router";

export default createStore({
  state: {
    goldCoinsBalance: 50000,
    sweepsCoinsBalance: 2000,
    vivamo: null,
    isLoggedIn: false,
    kycStatus: null,
    darkMode:
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  },
  mutations: {
    setLoginState(state, value) {
      state.isLoggedIn = value;
      state.goldCoinsBalance = 50000; // Reset gold coins balance on login
      state.sweepsCoinsBalance = 300000; // Reset sweeps coins balance on login
    },
    setDarkMode(state, value) {
      state.darkMode = value;
    },
    setVivamo(state, vivamoInstance) {
      state.vivamo = vivamoInstance;
    },
    setKycStatus(state, status) {
      state.kycStatus = status;
    },
    increaseGoldCoinsBalance(state, points) {
      state.goldCoinsBalance += points;
    },
    decreaseGoldCoinsBalance(state, points) {
      state.goldCoinsBalance -= points;
    },
    increaseSweepsCoinsBalance(state, points) {
      state.sweepsCoinsBalance += points;
    },
    decreaseSweepsCoinsBalance(state, points) {
      state.sweepsCoinsBalance -= points;
    },
  },
  actions: {
    toggleDarkMode({ commit, state }) {
      commit("setDarkMode", !state.darkMode);
    },
    login({ commit }) {
      console.log("Login action triggered");
      commit("setLoginState", true);
    },
    logout({ commit }) {
      // Remove user data from localStorage
      localStorage.removeItem("demoUserData");

      // Clear persisted Vuex state
      localStorage.removeItem("vivavegas-demo");

      // Reset login state
      commit("setLoginState", false);

      // Clear Vivamo instance
      commit("setVivamo", null);
      commit("setKycStatus", null);

      // Redirect to games page if on purchase or withdraw screens
      const currentPath = router.currentRoute.value.path;
      if (currentPath === "/purchase" || currentPath === "/withdraw") {
        router.push("/games");
      }
    },
    initializeVivamo({ commit, state }) {
      if (state.vivamo && typeof state.vivamo.on === "function") {
        return Promise.resolve(state.vivamo);
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = process.env.VUE_APP_VIVAMO_SCRIPT_SRC;
        script.async = true;
        script.onload = () => {
          const vivamoInstance = new Vivamo(
            process.env.VUE_APP_VIVAMO_PUBLISHABLE_KEY,
            {
              environment: "test",
              apiHost: process.env.VUE_APP_CLIENT_API_HOST,
            },
          );

          vivamoInstance.on("payment-created", (data) => {
            console.log("_emit: Payment created: ", data);
          });
          vivamoInstance.on("payment-completed", () => {
            console.log("_emit: Payment completed");
          });
          vivamoInstance.on("kyc-completed", (data) => {
            console.log("_emit: KYC completed", data);
            commit("setKycStatus", data?.status || null);
          });

          commit("setVivamo", vivamoInstance);
          resolve(vivamoInstance);
        };
        script.onerror = reject;
        document.body.appendChild(script);
      });
    },
    purchase({ commit }, { goldCoins, sweepsCoins }) {
      commit("increaseGoldCoinsBalance", parseInt(goldCoins));
      commit("increaseSweepsCoinsBalance", parseInt(sweepsCoins));
    },
    withdraw({ commit }, { coinType, points }) {
      if (coinType === "gold") {
        commit("decreaseGoldCoinsBalance", parseInt(points));
      } else if (coinType === "sweeps") {
        commit("decreaseSweepsCoinsBalance", parseInt(points));
      }
    },
    playGame({ commit }, { points, coinType }) {
      console.log("Playing game with points:", points, "coinType:", coinType);
      if (coinType === "gold") {
        commit("decreaseGoldCoinsBalance", parseInt(points));
      } else if (coinType === "sweeps") {
        commit("decreaseSweepsCoinsBalance", parseInt(points));
      }
    },
  },
  getters: {
    vivamo: (state) => state.vivamo,
  },
  plugins: [
    createPersistedState({
      key: "vivavegas-demo",
      storage: window.localStorage,
      reducer: (state) => {
        // Don't persist SDK instance (not serializable) or ephemeral KYC status.
        // eslint-disable-next-line no-unused-vars
        const { vivamo, kycStatus, ...rest } = state;
        return rest;
      },
    }),
  ],
});
